import axios from "axios";
import { ENDPOINT_STORAGE_TEMP_UPLOAD, ENDPOINT_STORAGE_MARK_USED } from "../../constants";
import { ApiResponse } from "../types";
import { handleApiError } from "../../utils/error_handler";

export interface TemporaryUploadResponse {
  url: string;
  expires_in: number;
  message: string;
}

export interface MarkAsUsedResponse {
  message: string;
  permanent_url: string;
}

export interface MarkAsUsedRequest {
  url: string;
  permanent_path?: string;
}

/**
 * Upload a file to temporary storage with automatic expiration
 * @param file - The file to upload
 * @returns The public URL and expiration info
 */
export async function uploadTemporaryFile(file: File): Promise<TemporaryUploadResponse> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const res = await axios.post<ApiResponse<TemporaryUploadResponse>>(
      ENDPOINT_STORAGE_TEMP_UPLOAD,
      formData,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (res.data.Code !== '200') {
      throw handleApiError({ response: { data: res.data } });
    }

    return res.data.Msg as TemporaryUploadResponse;
  } catch (error: any) {
    throw handleApiError(error);
  }
}

/**
 * Mark a temporary file as used, moving it to permanent storage
 * @param url - The temporary file URL
 * @param permanentPath - Optional permanent path (defaults to "permanent")
 * @returns The new permanent URL
 */
export async function markFileAsUsed(url: string, permanentPath?: string): Promise<MarkAsUsedResponse> {
  try {
    const payload: MarkAsUsedRequest = {
      url,
      ...(permanentPath && { permanent_path: permanentPath }),
    };

    const res = await axios.post<ApiResponse<MarkAsUsedResponse>>(
      ENDPOINT_STORAGE_MARK_USED,
      payload,
      { withCredentials: true }
    );

    if (res.data.Code !== '200') {
      throw handleApiError({ response: { data: res.data } });
    }

    return res.data.Msg as MarkAsUsedResponse;
  } catch (error: any) {
    throw handleApiError(error);
  }
}

/**
 * Helper function to upload a file temporarily and immediately mark it as used
 * @param file - The file to upload
 * @param permanentPath - Optional permanent path
 * @returns The permanent URL
 */
export async function uploadAndMarkAsUsed(file: File, permanentPath?: string): Promise<string> {
  try {
    // First upload as temporary
    const uploadResponse = await uploadTemporaryFile(file);
    
    // Then mark as used
    const markResponse = await markFileAsUsed(uploadResponse.url, permanentPath);
    
    return markResponse.permanent_url;
  } catch (error) {
    throw error;
  }
}